import { chatClientApiBaseUrl } from "@config/config";

export type MessageAttachment = {
  type: "file";
  filename: string;
  mediaType?: string;
  url: string;
};

/** 当前环境对应的聊天附件 API base URL */
export const getChatApiBaseUrl = (): string => {
  const env = process.env.NEXT_PUBLIC_ENV as keyof typeof chatClientApiBaseUrl;
  return chatClientApiBaseUrl[env] || chatClientApiBaseUrl.prod;
};

/** 从 sandbox / attachment-temp 协议 URL 解析可下载文件名 */
export const getDownloadableFileName = (url: string): string => {
  if (url?.startsWith("sandbox:")) {
    return url.replace("sandbox:", "").split("/").pop() || "";
  }
  if (url?.startsWith("attachment-temp://")) {
    return url.replace("attachment-temp://", "").split("/").pop() || "";
  }
  return "";
};

export type BuildAttachmentUrlParams = {
  baseUrl: string;
  fileName: string;
  shareId?: string;
  userId?: string;
  chatId?: string;
};

/** 构建附件下载/预览请求 URL（share 与普通会话两种路径） */
export const buildAttachmentRequestUrl = ({
  baseUrl,
  fileName,
  shareId,
  userId,
  chatId,
}: BuildAttachmentUrlParams): string => {
  if (shareId) {
    return `${baseUrl}/share/${encodeURIComponent(shareId)}/attachments/${encodeURIComponent(fileName)}`;
  }
  if (!userId || !chatId) {
    return "";
  }
  return `${baseUrl}/attachments/${encodeURIComponent(
    userId,
  )}/${encodeURIComponent(chatId)}/${encodeURIComponent(fileName)}`;
};

export const isImageAttachment = (attachment: MessageAttachment): boolean => {
  return (
    /^image\//i.test(attachment.mediaType || "") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(attachment.filename || "")
  );
};

export const getAttachmentKey = (attachment: MessageAttachment): string =>
  `${attachment.url}-${attachment.filename}`;

/** 可直接用于 img src 的公开 URL（http(s) 或 data URI） */
export const getPublicPreviewUrl = (url: string): string => {
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) {
    return url;
  }
  return "";
};

export type MessageAttachmentPreviewContext = {
  baseUrl: string;
  shareId?: string;
  userId?: string;
  chatId?: string;
  headers?: HeadersInit;
  /** 便于单测注入 mock fetch */
  fetchImpl?: typeof fetch;
};

export type AttachmentPreviewEntry = {
  key: string;
  url: string;
};

const defaultFetch: typeof fetch = (...args) => fetch(...args);

/**
 * 解析单条消息附件的预览 URL（公开 URL 或鉴权 fetch 后的 blob URL）。
 * 失败或无法解析时返回 null；blob URL 由调用方在不用时 revoke。
 */
const resolveMessageAttachmentPreviewUrl = async (
  attachment: MessageAttachment,
  context: MessageAttachmentPreviewContext,
): Promise<AttachmentPreviewEntry | null> => {
  const key = getAttachmentKey(attachment);
  const publicUrl = getPublicPreviewUrl(attachment.url);
  if (publicUrl) {
    return { key, url: publicUrl };
  }

  const downloadFileName = getDownloadableFileName(attachment.url);
  if (
    !downloadFileName ||
    (!context.shareId && (!context.userId || !context.chatId))
  ) {
    return null;
  }

  const requestUrl = buildAttachmentRequestUrl({
    baseUrl: context.baseUrl,
    fileName: downloadFileName,
    shareId: context.shareId,
    userId: context.userId,
    chatId: context.chatId,
  });
  if (!requestUrl) {
    return null;
  }

  const fetchFn = context.fetchImpl ?? defaultFetch;

  try {
    const response = await fetchFn(requestUrl, {
      headers: context.headers,
    });
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    return { key, url: blobUrl };
  } catch {
    return null;
  }
};

/** 顺序解析多条附件预览 URL，跳过解析失败的项 */
export const resolveMessageAttachmentPreviewUrls = async (
  attachments: MessageAttachment[],
  context: MessageAttachmentPreviewContext,
): Promise<AttachmentPreviewEntry[]> => {
  const loadedEntries: AttachmentPreviewEntry[] = [];

  for (const attachment of attachments) {
    const entry = await resolveMessageAttachmentPreviewUrl(attachment, context);
    if (entry) {
      loadedEntries.push(entry);
    }
  }

  return loadedEntries;
};

/** 撤销批量预览结果中的 blob URL */
export const revokeBlobPreviewEntries = (
  entries: Array<{ url: string }>,
): void => {
  entries.forEach((entry) => {
    if (entry.url.startsWith("blob:")) {
      URL.revokeObjectURL(entry.url);
    }
  });
};
